/* eslint-disable @typescript-eslint/no-use-before-define */
import axios from "axios";
import { action, computed, makeAutoObservable } from "mobx";
import { TokenResponse } from "../../api/DTOtemp";
import { InternalAuthenticationProviders } from "../../api/clients/authenticationProviders/DTO";
import SessionData from "./SessionData";

// 1000 ms -> 60s -> 10m
const pollingPeriod = 1000 * 60 * 10

// if another service is polling we use this to avoid duplicating 
// the requests 
// 1000 ms -> 60s -> 11m
const delayedPollingPeriod = 1000 * 60 * 11

// if we're loading the service then use a quick 1s refresh
// if we don't do this then the history service instantiates
// and requests a value from us before we're done
const quickRefreshDelay = 1000

type StorageEntry<T> = {

	sourceId: number
	value: T

}

export class SessionService {

	// current time in nanoseconds, used to register our id for other tabs
	// https://developer.mozilla.org/en-US/docs/Web/API/Performance/now
	public static readonly WindowId = window.performance?.now() ?? 0

	private static instance: SessionService | null

	public static getInstance(): SessionService {
		if (SessionService.instance == null)
			SessionService.instance = new this()
		return SessionService.instance
	}

	public get userId(): number {
		const userId = SessionData.accessToken.tokenPayload?.sub;
		if (userId === undefined)
			throw new Error('auth token not provided, user id not set')

		return userId;
	}

	private constructor() {
		makeAutoObservable<SessionService>(this, {
			isAuthenticated: computed,
			isSignedInUser: computed,
			isAnonymous: computed,
			logout: action,
		});

		this.registerTokenListeners()
	}

	/**
	 * whether or not the current session is authenticated
	 */
	public get isAuthenticated(): boolean {
		return !SessionData.accessToken.isInvalidOrExpired();
	}

	public get isAnonymous(): boolean {
		return !SessionData.accessToken.isInvalidOrExpired() && (SessionData.accessToken.tokenPayload?.anonymous ?? false);
	}

	/**
	 * signed in as a FIM user - not just authenticated
	 */
	public get isSignedInUser(): boolean {
		return !SessionData.accessToken.isInvalidOrExpired() && (!SessionData.accessToken.tokenPayload?.anonymous ?? false)
	}

	public logout(): void {
		SessionData.logout()
	}

	private registerTokenListeners(): void {
		// TODO this needs to return a function that wraps `this` as a closure

		const tokenUpdatedEvent = (e: StorageEvent): void => {


			if (e.key === SessionData.idToken.tokenName)
				SessionData.idToken.setToken(e.newValue)

			if (e.key === SessionData.accessToken.tokenName)
				SessionData.accessToken.setToken(e.newValue)

			if (e.key === SessionData.refreshToken.tokenName)
				SessionData.refreshToken.setToken(e.newValue)
		}

		window.addEventListener('storage', tokenUpdatedEvent, false)
	}


	public async signInAnonymous(): Promise<void> {
		const response = await axios.post<TokenResponse>(
			`${window.location.origin}/api/v1/AuthenticationProviderInstances/${InternalAuthenticationProviders.FimAnonymousId}/Login`,
		)

		const tokens = response.data as TokenResponse
		SessionData.idToken.setToken(tokens.idToken)
		SessionData.accessToken.setToken(tokens.accessToken)
		if (tokens.refreshToken)
			SessionData.refreshToken.setToken(tokens.refreshToken)
	}
}


